import { ImagePlus, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { fr } from '@/i18n/fr'

/** Mirrors `recipes.photos.MAX_UPLOAD_BYTES`. Checked here so the refusal arrives before the
 *  upload rather than after it, which on a phone connection is a slow way to learn. The
 *  server still enforces it — this is the compiler saying it first, as with F-15. */
const MAX_BYTES = 15 * 1024 * 1024

interface Props {
  /** The stored photo, if the recipe already has one. */
  current: string | null
  /** A file picked in this session and not yet uploaded. */
  picked: File | null
  onPick: (file: File | null) => void
  /** True once the existing photo is marked for removal on save. */
  removed: boolean
  onRemove: () => void
}

/**
 * The photo half of the editor (F-20). It picks a file and nothing else — the upload happens
 * after the recipe is saved, because a recipe being created has no id to attach a file to.
 */
export function PhotoField({ current, picked, onPick, removed, onRemove }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!picked) {
      setPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(picked)
    setPreviewUrl(url)
    // An object URL pins the file in memory until it is revoked.
    return () => URL.revokeObjectURL(url)
  }, [picked])

  const shown = previewUrl ?? (removed ? null : current)

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Let the same file be picked again after a removal.
    event.target.value = ''
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError(fr.recipeEdit.photo.notAnImage)
      return
    }
    if (file.size > MAX_BYTES) {
      setError(fr.recipeEdit.photo.tooLarge(MAX_BYTES / (1024 * 1024)))
      return
    }
    setError(null)
    onPick(file)
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium">{fr.recipeEdit.photo.label}</span>

      {shown && (
        <img
          src={shown}
          alt=""
          className="aspect-[16/9] w-full rounded-2xl border border-stone-200 object-cover dark:border-stone-800"
        />
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
          <ImagePlus className="h-4 w-4" />
          {shown ? fr.recipeEdit.photo.replace : fr.recipeEdit.photo.choose}
        </Button>
        {shown && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setError(null)
              onPick(null)
              onRemove()
            }}
            className="text-stone-500"
          >
            <Trash2 className="h-4 w-4" />
            {fr.recipeEdit.photo.remove}
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* No `capture` attribute on purpose: it forces the camera and hides the library on
          iOS, and the common case is a photo taken ten minutes ago, not one being taken
          now. `.heic`/`.heif` are named explicitly because some Android pickers do not
          match them against `image/*` (NFR-20). */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={handleChange}
        className="hidden"
        aria-label={fr.recipeEdit.photo.choose}
      />
    </div>
  )
}
